import { useCallback } from 'react';
import { PickListItem } from './PickList';

interface SelectionRange {
  start: number;
  end: number;
}

function getSelectionRange<T extends PickListItem>(
  items: T[],
  lastSelectedId: string,
  currentId: string,
): SelectionRange | null {
  const lastIndex = items.findIndex((item) => item.id === lastSelectedId);
  const currentIndex = items.findIndex((item) => item.id === currentId);

  if (lastIndex === -1 || currentIndex === -1) {
    return null;
  }

  return {
    start: Math.min(lastIndex, currentIndex),
    end: Math.max(lastIndex, currentIndex),
  };
}

function selectRange<T extends PickListItem>(
  items: T[],
  range: SelectionRange,
  selection: Set<string>,
): void {
  for (let i = range.start; i <= range.end; i++) {
    const item = items[i];
    if (item) {
      selection.add(item.id);
    }
  }
}

function toggleSelection(itemId: string, selection: Set<string>): void {
  if (selection.has(itemId)) {
    selection.delete(itemId);
    return;
  }
  selection.add(itemId);
}

export function useItemSelection<T extends PickListItem>(
  items: T[],
  currentSelection: Set<string>,
) {
  const handleItemClick = useCallback(
    (itemId: string, event: React.MouseEvent): Set<string> => {
      const newSelection = new Set(currentSelection);
      const isMultiSelect = event.ctrlKey || event.metaKey;
      const isRangeSelect = event.shiftKey;

      if (isMultiSelect) {
        toggleSelection(itemId, newSelection);
        return newSelection;
      }

      if (isRangeSelect && currentSelection.size > 0) {
        const lastSelectedId =
          Array.from(currentSelection)[currentSelection.size - 1];
        const range = getSelectionRange(items, lastSelectedId, itemId);

        if (range) {
          selectRange(items, range, newSelection);
        }
        return newSelection;
      }

      newSelection.clear();
      newSelection.add(itemId);
      return newSelection;
    },
    [currentSelection, items],
  );

  return { handleItemClick };
}
